import classNames from 'classnames';
import React, {
  FC, Fragment, useCallback, useMemo, useState,
} from 'react';
import getClassName from 'src/common/utils/getClassName';
import Icon from '../IconDS22';
import type { NavItemType } from '../Navigation/types';
import NavItem from '../Navigation/NavItem';
import styles from './NestedMenu.module.scss';

export type NestedMenuProps = {
  items: NavItemType[];
  variant?: 'dropdown' | 'sidebar';
};

const traverseItems = (
  items: NavItemType[],
  callback: (item: NavItemType, parent: NavItemType) => void,
  parent?: NavItemType,
) => {
  items.forEach((item) => {
    if ('key' in item) {
      callback(item, parent || item);
      if (item.children) {
        traverseItems(item.children, callback, item);
      }
    }
  });
};

const NestedMenu: FC<NestedMenuProps> = ({
  items,
  variant,
}) => {
  const [activeItem, setActiveItem] = useState<NavItemType>(items[0]);
  const handleClick = useCallback((item: NavItemType) => {
    setActiveItem(item);
  }, []);
  const onBackClick = useCallback(() => {
    traverseItems(items, (item, parent) => {
      if (item.key === activeItem.key) {
        setActiveItem(parent);
      }
    });
  }, [items, activeItem.key]);
  const activeItemChildren = useMemo(() => activeItem?.children || [], [activeItem]);
  const rootKey = (items[0]).key;
  const atRoot = useMemo(() => activeItem.key === rootKey, [activeItem, rootKey]);
  const title = activeItem.label;

  return (
    <li className={getClassName(styles, 'root', variant)}>
      {!atRoot && (
        <button type="button" className={styles.backTitle} onClick={onBackClick}>
          <Icon id="arrow-left" />
          {title}
        </button>
      )}
      <ul className={styles.list}>
        {activeItemChildren.map((item) => (
          <Fragment key={item.key}>
            {item.children && (
              <li className={getClassName(styles, 'item', classNames({ sectionDivider: item.sectionDivider }))}>
                <button type="button" onClick={() => handleClick(item)}>
                  {item.icon && <Icon id={item.icon} />}
                  {item.label}
                  <Icon id="caret-right" size="s" />
                </button>
              </li>
            )}
            {'path' in item && (
              <li className={getClassName(styles, 'item', classNames({ sectionDivider: item.sectionDivider }))}>
                <NavItem
                  to={item.path}
                  icon={item.icon}
                  openInNewTab={item.openInNewTab}
                  variant="subNav"
                  disabled={item.disabled}
                  classNameModifier="subNav"
                >
                  {item.label}
                </NavItem>
              </li>
            )}
            {'component' in item && (
              <li className={getClassName(styles, 'item', classNames({ sectionDivider: item?.sectionDivider }))}>
                <div className={styles.componentWrapper}>
                  {item.component}
                </div>
              </li>
            )}
          </Fragment>
        ))}
      </ul>
    </li>
  );
};

export default NestedMenu;
